/**
 * ExportFormatButtons - Reusable grid of format selection buttons
 *
 * Used across Collection, Deck, and List export modals for consistent UX.
 * Displays format options in a grid with icon, label, and description.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

import type { FormatOption } from '../types';

// ============================================================================
// Types
// ============================================================================

export interface ExportFormatButtonsProps<T extends string = string> {
  /** Available format options to display */
  options: FormatOption<T>[];
  /** Currently selected format value */
  value: T;
  /** Callback when format selection changes */
  onChange: (value: T) => void;
  /** Number of columns in the grid (default: 2) */
  columns?: 2 | 3;
  /** Optional label text (default: "Export Format") */
  label?: string;
  /** Whether to show the label (default: true) */
  showLabel?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export default function ExportFormatButtons<T extends string = string>({
  options,
  value,
  onChange,
  columns = 2,
  label = 'Export Format',
  showLabel = true,
}: ExportFormatButtonsProps<T>): ReactElement {
  return (
    <div className="space-y-3">
      {showLabel && <label className="text-pearl block text-sm font-medium">{label}</label>}

      <div
        className={clsx(
          'grid gap-2',
          columns === 2 && 'grid-cols-1 sm:grid-cols-2',
          columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={clsx(
                'flex items-start gap-3 rounded-lg border p-3 text-left transition-all',
                isSelected ? 'border-aether bg-aether/10 ring-aether/50 ring-1' : 'border-storm hover:border-ash hover:bg-slate/50',
              )}
            >
              <div className={clsx('shrink-0 rounded-lg p-2', isSelected ? 'bg-aether/20 text-aether' : 'bg-slate text-ash')}>
                {option.icon}
              </div>
              <div className="min-w-0 flex-1">
                <p className={clsx('font-medium', isSelected ? 'text-pearl' : 'text-silver')}>{option.label}</p>
                <p className="text-ash mt-0.5 text-xs">{option.description}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
